import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Page, Card, EmptyState, TH, TD } from "@/components/polaris";
import { Pill, ConfidenceBadge, InsufficientBadge } from "@/components/AnswerBits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollText, Download, FileText, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const humanize = (a) => (a || "").replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const toneFor = (a) =>
  a === "query_asked" ? "highlight"
    : a === "feedback_submitted" ? "warning"
    : /deleted|denied|cancel|failed|locked/.test(a || "") ? "critical"
    : "neutral";

async function exportCsv(params) {
  try {
    const r = await api.get("/audit/export", { params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `candi-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(apiErr(e));
  }
}

function FilterSelect({ value, onChange, options, allLabel, testid, format = (x) => x }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}
        className={`h-9 w-full sm:w-[220px] text-[13px] rounded-lg ${value !== ALL ? "bg-[#F0F7FF] border-[#BAE6FD] text-[#0369A1] font-medium" : "bg-white border-[#C9CCCF]"}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{format(o)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Section({ label, children }) {
  return (
    <div className="pt-4 border-t border-[#EDEEF0]">
      <div className="text-xs font-semibold text-[#6D7175] mb-2">{label}</div>
      {children}
    </div>
  );
}

function QueryDetail({ d }) {
  const filters = Object.entries(d.filters || {});
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-[#05304F] text-white px-4 py-3 text-[15px] leading-6" data-testid="audit-detail-question">{d.question}</div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-[#6D7175]">Candi's answer</span>
          {d.insufficient_evidence ? <InsufficientBadge /> : d.confidence && <ConfidenceBadge level={d.confidence} />}
        </div>
        <div className="text-sm leading-6 text-[#303030] space-y-2 [&_table]:w-full [&_table]:text-sm [&_th]:text-left [&_th]:border-b [&_th]:px-2 [&_th]:py-1 [&_td]:border-b [&_td]:px-2 [&_td]:py-1 [&_ul]:list-disc [&_ul]:pl-5 [&_strong]:font-semibold" data-testid="audit-detail-answer">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{d.answer}</ReactMarkdown>
        </div>
      </div>
      {filters.length > 0 && (
        <Section label="Filters used">
          <div className="flex flex-wrap gap-1.5">{filters.map(([k, v]) => <Pill key={k} tone="highlight">{humanize(k)}: {v}</Pill>)}</div>
        </Section>
      )}
      <Section label={`Sources (${d.sources.length})`}>
        {d.sources.length === 0 ? <p className="text-sm text-[#6D7175]">No sources were cited.</p> : (
          <div className="space-y-1.5" data-testid="audit-detail-sources">
            {d.sources.map((s, i) => (
              <div key={i} className="flex gap-2 items-start rounded-lg border border-[#E1E3E5] bg-[#F7F7F8] px-3 py-2 text-xs">
                <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#005180]" />
                <div className="min-w-0">
                  <div className="text-[#202223]"><span className="font-semibold">{s.lender}</span> · {s.document_title} · <span className="font-mono-cite text-[#005180]">{s.location}</span></div>
                  {s.section_path && <div className="text-[#6D7175] mt-0.5 break-words">{s.section_path}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section label="Feedback">
        {d.feedback.length === 0 ? <p className="text-sm text-[#6D7175]" data-testid="audit-detail-no-feedback">No feedback on this answer.</p> : (
          <div className="space-y-1.5" data-testid="audit-detail-feedback">
            {d.feedback.map((f, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap text-sm">
                <Pill tone={f.rating === "Helpful" ? "success" : f.issue_type ? "critical" : "warning"}>{f.rating}{f.issue_type ? ` — ${f.issue_type}` : ""}</Pill>
                <span className="text-[#45494D]">{f.user_email}</span>
                <span className="text-xs text-[#6D7175]">{fmtTime(f.created_at)}</span>
                {f.comment && <p className="w-full text-[#303030] italic">“{f.comment}”</p>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function DetailDialog({ log, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!log) return;
    setData(null);
    api.get(`/audit/${log.id}`).then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); onClose(); });
  }, [log]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col gap-4" data-testid="audit-detail-dialog">
        {log && (
          <DialogHeader className="text-left sm:text-left">
            <div className="flex items-center gap-2"><Pill tone={toneFor(log.action)}>{humanize(log.action)}</Pill></div>
            <DialogTitle className="text-lg pt-1">{log.action === "query_asked" ? "Logged query" : log.action === "feedback_submitted" ? "Answer feedback" : "Event details"}</DialogTitle>
            <DialogDescription>{log.user_email} · {fmtTime(log.created_at)}</DialogDescription>
          </DialogHeader>
        )}
        <div className="min-h-0 overflow-y-auto -mx-6 px-6 pb-1" data-testid="audit-detail-body">
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6D7175]" /></div>
        ) : data.detail ? (
          <QueryDetail d={data.detail} />
        ) : (
          <div className="space-y-3" data-testid="audit-detail-summary">
            <p className="text-[15px] text-[#202223]">{data.log.summary}</p>
            {log?.has_detail && <p className="text-sm text-[#6D7175]">The answer for this entry is no longer stored.</p>}
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [facets, setFacets] = useState({ actions: [], users: [] });
  const [action, setAction] = useState(ALL);
  const [userEmail, setUserEmail] = useState(ALL);
  const [open, setOpen] = useState(null);

  const params = {};
  if (action !== ALL) params.action = action;
  if (userEmail !== ALL) params.user_email = userEmail;

  useEffect(() => { api.get("/audit/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/audit", { params })
      .then((r) => { setLogs(r.data.items); setTotal(r.data.total); })
      .catch((e) => toast.error(apiErr(e))).finally(() => setLoaded(true));
  }, [action, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get("/audit", { params: { ...params, skip: logs.length } });
      setLogs((prev) => [...prev, ...r.data.items]);
      setTotal(r.data.total);
    } catch (e) { toast.error(apiErr(e)); }
    setLoadingMore(false);
  };

  const filtered = action !== ALL || userEmail !== ALL;

  return (
    <Layout>
      <Page title="Audit log" subtitle="Uploads, indexing runs, queries, and feedback events. Click any row for details."
        actions={<Button variant="outline" size="sm" onClick={() => exportCsv(params)} disabled={!total} data-testid="audit-export-csv"
          className="gap-1.5 bg-white border-[#C9CCCF] hover:bg-[#F1F2F4] rounded-lg"><Download className="h-4 w-4" /> Export CSV</Button>}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3" data-testid="audit-filters">
          <FilterSelect value={action} onChange={setAction} options={facets.actions} allLabel="All events" testid="audit-filter-event" format={humanize} />
          <FilterSelect value={userEmail} onChange={setUserEmail} options={facets.users} allLabel="All users" testid="audit-filter-user" />
          {filtered && (
            <button type="button" onClick={() => { setAction(ALL); setUserEmail(ALL); }} data-testid="audit-clear-filters"
              className="self-start sm:self-auto text-sm font-medium text-[#005180] hover:bg-[#F0F7FF] rounded-md px-2 py-1">Clear filters</button>
          )}
          <span className="sm:ml-auto text-xs text-[#6D7175]" data-testid="audit-count">{logs.length < total ? `Showing ${logs.length} of ${total} events` : `${total} event${total === 1 ? "" : "s"}`}</span>
        </div>
        {loaded && logs.length === 0 ? (
          <EmptyState icon={ScrollText} data-testid="empty-audit">{filtered ? "No events match these filters." : "No audit events yet."}</EmptyState>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr><th className={TH}>Event</th><th className={TH}>User</th><th className={TH}>Details</th><th className={TH}>Time</th><th className={TH}><span className="sr-only">Open</span></th></tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} onClick={() => setOpen(l)} data-testid={`audit-${l.id}`}
                      className="cursor-pointer hover:bg-[#F7F7F8] transition-colors group">
                      <td className={`${TD} whitespace-nowrap`}><Pill tone={toneFor(l.action)}>{humanize(l.action)}</Pill></td>
                      <td className={`${TD} text-[#45494D] whitespace-nowrap`}>{l.user_email}</td>
                      <td className={`${TD} max-w-[360px] xl:max-w-[420px]`}>
                        <span className={`block truncate ${l.action === "query_asked" ? "text-[#202223]" : "text-[#45494D]"}`} title={l.summary}>{l.summary}</span>
                      </td>
                      <td className={`${TD} whitespace-nowrap text-xs text-[#6D7175]`}>{fmtTime(l.created_at)}</td>
                      <td className={`${TD} w-8 text-right`}><ChevronRight className="h-4 w-4 text-[#8C9196] group-hover:text-[#202223]" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {logs.length < total && (
              <div className="p-3 flex justify-center border-t border-[#EDEEF0]">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="audit-load-more"
                  className="gap-1.5 bg-white border-[#C9CCCF] hover:bg-[#F1F2F4] rounded-lg">
                  {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Load more
                </Button>
              </div>
            )}
          </Card>
        )}
      </Page>
      <DetailDialog log={open} onClose={() => setOpen(null)} />
    </Layout>
  );
}
